import type { RemoteWidgetType } from '@highimpact-front/remote-widget-core';
import type { ApiReturnSite } from '@highimpact-frontend/api-types';
import { ConfigDrawer } from '@highimpact/dashboard-ui';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';

import { useDashboardHost } from '../../context/DashboardHostContext';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../../runtime/DashboardLibraryProvider';
import { setEditing, setWidget } from '../../state/dashboard/slice';
import type { WidgetType } from '../../state/dashboard/widget';
import RemoteConfigPanel from './RemoteConfigPanel';

export interface RemoteConfigSidePanelProps {
  readonly site?: ApiReturnSite;
  readonly project_id?: number;
}

export default function RemoteConfigSidePanel({
  site,
  project_id,
}: RemoteConfigSidePanelProps) {
  const dispatch = useDashboardLibraryDispatch();
  const host = useDashboardHost();

  const editing = useDashboardLibrarySelector(
    (state) => state.dashboard.editing
  );

  const widget = useDashboardLibrarySelector((state) =>
    editing.state !== 'NotEditing'
      ? state.dashboard[editing.gridType][editing.gridId]?.widgets[
          editing.widgetId
        ]
      : undefined
  );

  const widgetId =
    editing.state !== 'NotEditing' ? editing.widgetId : undefined;
  const gridId = editing.state !== 'NotEditing' ? editing.gridId : undefined;
  const gridType =
    editing.state !== 'NotEditing' ? editing.gridType : undefined;

  useEffect(() => {
    dispatch(setEditing({ state: 'NotEditing' }));
  }, [dispatch, site?.id]);

  if (editing.state === 'NotEditing') {
    return null;
  }

  const handleClose = () => dispatch(setEditing({ state: 'NotEditing' }));

  const isRemoteWidgetEditing =
    widget?.type === 'remote-widget' &&
    widgetId !== undefined &&
    gridId !== undefined &&
    gridType !== undefined;

  const subtitle =
    isRemoteWidgetEditing && widget.props.name !== undefined ? (
      <Typography variant="caption" color="text.secondary" component="p">
        {widget.props.name}
      </Typography>
    ) : undefined;

  return (
    <ConfigDrawer
      open
      onClose={handleClose}
      title="Configuration"
      subtitle={subtitle}
    >
      {isRemoteWidgetEditing ? (
        <RemoteConfigPanel
          widgetId={widgetId}
          widget={widget as RemoteWidgetType}
          setWidget={(nextWidget) => {
            dispatch(
              setWidget({
                widget: nextWidget as WidgetType,
                widgetId,
                gridId,
                gridType,
              })
            );
            void host.api.dashboard.putDashboardElement(
              nextWidget.dashboardElementId,
              nextWidget.elementData
            );
          }}
          // TODO: remove from props typing
          setDisplayer={() => {
            // Remote widget config persists via element JSON, not displayer slice.
          }}
          site={site}
          project_id={project_id}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Configuration non disponible pour ce type d&apos;élément.
        </Typography>
      )}
    </ConfigDrawer>
  );
}
